import { useEffect, useState, useMemo } from "react";
import api, { formatApiError, fileToBase64 } from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Package, Loader2, Sparkles, Upload, X, Check, Gift, Search } from "lucide-react";
import { toast } from "sonner";

export const GIFT_OPTIONS = [
  "Borsa Notebook protettiva",
  "Panno copri tastiera, in microfibra proteggi Monitor",
  "Kit Tastiera + Mouse Logitech Wireless",
];

const SPEC_FIELDS = [
  ["processore", "Processore"],
  ["ram", "RAM"],
  ["hdd", "Storage"],
  ["monitor", "Monitor"],
  ["scheda_video", "Scheda Video"],
  ["sistema_operativo", "Sistema Operativo"],
];

const emptyForm = () => ({
  title: "", price: 0, quantity: 1, description: "", image: "", gallery: [],
  condition: "nuovo", discount: 0, gifts: [], specs: {},
});

export default function CatalogProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [imgLoading, setImgLoading] = useState(false);
  const [varLoading, setVarLoading] = useState(false);
  const [varCount, setVarCount] = useState(3);
  const [customGift, setCustomGift] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    api.get("/catalog").then((r) => setProducts(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => [p.title, p.description].some((f) => (f || "").toLowerCase().includes(q)));
  }, [products, search]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updSpec = (k, v) => setForm((f) => ({ ...f, specs: { ...f.specs, [k]: v } }));

  const openNew = () => { setEditingId(null); setForm(emptyForm()); setCustomGift(""); setOpen(true); };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({ title: p.title, price: p.price, quantity: p.quantity || 1, description: p.description || "",
      image: p.image || "", gallery: p.gallery || [], condition: p.condition || "nuovo", discount: p.discount || 0,
      gifts: p.gifts || [], specs: p.specs || {} });
    setCustomGift(""); setOpen(true);
  };

  const genAI = async () => {
    if (!form.title.trim()) { toast.error("Inserisci prima il titolo"); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/generate-description", {
        title: form.title, price: Number(form.price) || null, currency: "EUR", condition: form.condition,
      });
      upd("description", data.description);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore AI"); }
    finally { setAiLoading(false); }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Immagine troppo grande (max 5MB)"); return; }
    setImgLoading(true);
    try {
      const dataUrl = await fileToBase64(file);
      const { data } = await api.post("/images/process", { image: dataUrl });
      upd("image", data.image);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore immagine"); }
    finally { setImgLoading(false); }
  };

  const uploadGallery = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setImgLoading(true);
    try {
      const processed = [];
      for (const file of list) {
        if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name}: troppo grande (max 5MB)`); continue; }
        const dataUrl = await fileToBase64(file);
        const { data } = await api.post("/images/process", { image: dataUrl });
        processed.push(data.image);
      }
      setForm((f) => ({ ...f, gallery: [...(f.gallery || []), ...processed] }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore immagini"); }
    finally { setImgLoading(false); }
  };

  const genVariations = async () => {
    const refs = [form.image, ...(form.gallery || [])].filter(Boolean);
    if (!refs.length) { toast.error("Carica prima almeno una foto del prodotto"); return; }
    setVarLoading(true);
    try {
      const { data } = await api.post("/ai/generate-image-variations", {
        title: form.title, images: refs, count: varCount,
      });
      setForm((f) => ({ ...f, gallery: [...(f.gallery || []), ...(data.images || [])] }));
      toast.success(`${(data.images || []).length} varianti generate`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore varianti AI"); }
    finally { setVarLoading(false); }
  };

  const addCustomGift = () => {
    const v = customGift.trim();
    if (!v) return;
    if (!form.gifts.includes(v)) upd("gifts", [...form.gifts, v]);
    setCustomGift("");
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Il titolo è obbligatorio"); return; }
    setSaving(true);
    const payload = { ...form, price: Number(form.price) || 0, quantity: Number(form.quantity) || 1, discount: Number(form.discount) || 0 };
    try {
      if (editingId) {
        const { data } = await api.put(`/catalog/${editingId}`, payload);
        setProducts((ps) => ps.map((p) => (p.id === editingId ? data : p)));
        toast.success("Prodotto aggiornato");
      } else {
        const { data } = await api.post("/catalog", payload);
        setProducts((ps) => [...ps, data].sort((a, b) => a.title.localeCompare(b.title)));
        toast.success("Prodotto salvato nel catalogo");
      }
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const confirmRemove = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/catalog/${deleteId}`);
      setProducts((ps) => ps.filter((p) => p.id !== deleteId));
      toast.success("Prodotto eliminato");
    } catch { toast.error("Errore eliminazione"); }
    finally { setDeleting(false); setDeleteId(null); }
  };

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-black text-slate-900">Catalogo Prodotti</h1>
          <p className="text-sm text-slate-500 mt-1">Prodotti precompilati riutilizzabili nelle offerte.</p>
        </div>
        <Button onClick={openNew} data-testid="catalog-new-button" className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" /> Nuovo prodotto
        </Button>
      </div>

      <div className="relative max-w-sm mb-5">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="catalog-search" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca nel catalogo…" className="pl-9" />
      </div>

      {loading ? (
        <p className="text-slate-500">Caricamento…</p>
      ) : products.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-16 text-center">
          <Package className="w-10 h-10 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-900 font-semibold">Catalogo vuoto</p>
          <p className="text-slate-500 text-sm mt-1 mb-6">Crea il tuo primo prodotto precompilato (es. Office 2021).</p>
          <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-2" /> Crea prodotto</Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="catalog-grid">
          {filtered.map((p) => (
            <div key={p.id} data-testid={`catalog-card-${p.id}`}
              className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col">
              <div className="h-36 bg-slate-50 flex items-center justify-center">
                {p.image ? <img src={p.image} alt={p.title} className="w-full h-full object-contain" />
                  : <Package className="w-10 h-10 text-slate-200" />}
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-slate-900 leading-snug">{p.title}</h3>
                  <span className="text-sm font-bold text-slate-900 whitespace-nowrap">€ {Number(p.price).toFixed(2)}</span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {p.condition === "ricondizionato" && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-teal-100 text-teal-700">Ricondizionato</span>}
                  {p.discount > 0 && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">-{p.discount}%</span>}
                  {(p.gifts || []).length > 0 && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{p.gifts.length} omaggio</span>}
                </div>
                {p.description && <p className="text-xs text-slate-500 mt-2 line-clamp-2">{p.description}</p>}
                <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(p)} data-testid={`catalog-edit-${p.id}`}>
                    <Pencil className="w-3.5 h-3.5 mr-1.5" /> Modifica
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setDeleteId(p.id)} data-testid={`catalog-delete-${p.id}`}
                    className="text-slate-500 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="catalog-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifica prodotto" : "Nuovo prodotto"}</DialogTitle>
            <DialogDescription>Compila i campi del prodotto da salvare nel catalogo riutilizzabile.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-6">
                <Label className="text-xs">Titolo *</Label>
                <Input data-testid="catalog-form-title" value={form.title} onChange={(e) => upd("title", e.target.value)}
                  placeholder="Es. Microsoft Office 2021" className="mt-1" />
              </div>
              <div className="col-span-3">
                <Label className="text-xs">Prezzo</Label>
                <Input type="number" min="0" step="0.01" data-testid="catalog-form-price" value={form.price}
                  onChange={(e) => upd("price", e.target.value)} className="mt-1 text-right" />
              </div>
              <div className="col-span-3">
                <Label className="text-xs">Sconto %</Label>
                <Input type="number" min="0" max="100" step="0.5" data-testid="catalog-form-discount" value={form.discount}
                  onChange={(e) => upd("discount", e.target.value)} className="mt-1 text-right" />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500">Condizione:</Label>
              {[["nuovo", "Nuovo"], ["ricondizionato", "Ricondizionato"]].map(([val, lbl]) => (
                <button key={val} type="button" data-testid={`catalog-form-condition-${val}`}
                  onClick={() => upd("condition", val)}
                  className={`text-xs font-semibold px-3 py-1 rounded-full border transition-colors ${
                    form.condition === val ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  }`}>{lbl}</button>
              ))}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">Descrizione</Label>
                <Button type="button" variant="ghost" size="sm" onClick={genAI} disabled={aiLoading} data-testid="catalog-form-ai">
                  {aiLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />} Genera con AI
                </Button>
              </div>
              <Textarea data-testid="catalog-form-description" value={form.description} onChange={(e) => upd("description", e.target.value)}
                rows={3} placeholder="Descrizione commerciale del prodotto" />
            </div>

            <div>
              <Label className="text-xs">Specifiche tecniche</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {SPEC_FIELDS.map(([k, lbl]) => (
                  <Input key={k} data-testid={`catalog-form-spec-${k}`} value={form.specs[k] || ""}
                    onChange={(e) => updSpec(k, e.target.value)} placeholder={lbl} className="text-sm" />
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs flex items-center gap-1.5 mb-1.5"><Gift className="w-3.5 h-3.5" /> Prodotti in omaggio</Label>
              <div className="flex flex-col gap-1.5">
                {GIFT_OPTIONS.map((g) => {
                  const sel = form.gifts.includes(g);
                  return (
                    <button key={g} type="button" data-testid={`catalog-form-gift-${GIFT_OPTIONS.indexOf(g)}`}
                      onClick={() => upd("gifts", sel ? form.gifts.filter((x) => x !== g) : [...form.gifts, g])}
                      className={`flex items-center gap-2 text-left text-xs px-3 py-2 rounded-lg border transition-colors ${
                        sel ? "bg-teal-50 border-teal-300 text-teal-800" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}>
                      <span className={`w-4 h-4 rounded flex items-center justify-center border ${sel ? "bg-teal-600 border-teal-600" : "border-slate-300"}`}>
                        {sel && <Check className="w-3 h-3 text-white" />}
                      </span>{g}
                    </button>
                  );
                })}
                {form.gifts.filter((g) => !GIFT_OPTIONS.includes(g)).map((g) => (
                  <div key={g} className="flex items-center justify-between gap-2 text-xs px-3 py-2 rounded-lg border bg-teal-50 border-teal-300 text-teal-800">
                    <span className="flex items-center gap-2"><span className="w-4 h-4 rounded bg-teal-600 flex items-center justify-center"><Check className="w-3 h-3 text-white" /></span>{g}</span>
                    <button type="button" onClick={() => upd("gifts", form.gifts.filter((x) => x !== g))} className="text-teal-700 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <Input data-testid="catalog-form-gift-custom" value={customGift} onChange={(e) => setCustomGift(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomGift(); } }}
                  placeholder="Scrivi un omaggio a mano…" className="h-9 text-xs" />
                <Button type="button" variant="outline" size="sm" onClick={addCustomGift} data-testid="catalog-form-gift-add"><Plus className="w-4 h-4" /></Button>
              </div>
            </div>

            <div>
              <Label className="text-xs">Immagine principale</Label>
              <div className="mt-1 flex items-center gap-3">
                {form.image ? (
                  <div className="relative">
                    <img src={form.image} alt="" className="w-20 h-20 object-contain rounded-lg border border-slate-200" />
                    <button type="button" onClick={() => upd("image", "")} className="absolute -top-2 -right-2 bg-white rounded-full border border-slate-200 p-0.5 text-slate-500 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ) : (
                  <label className="cursor-pointer inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50">
                    {imgLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Carica immagine
                    <input type="file" accept="image/*" className="hidden" data-testid="catalog-form-image"
                      onChange={(e) => uploadImage(e.target.files?.[0])} />
                  </label>
                )}
              </div>

              <div className="mt-4">
                <Label className="text-xs">Altre immagini (puoi caricarne più di una)</Label>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  {(form.gallery || []).map((g, gi) => (
                    <div key={gi} className="relative">
                      <img src={g} alt="" className="w-16 h-16 object-contain rounded-lg border border-slate-200" data-testid={`catalog-gallery-img-${gi}`} />
                      <button type="button" onClick={() => upd("gallery", form.gallery.filter((_, i) => i !== gi))}
                        className="absolute -top-2 -right-2 bg-white rounded-full border border-slate-200 p-0.5 text-slate-500 hover:text-red-600"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                  <label className="cursor-pointer inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg border border-dashed border-slate-300 hover:bg-slate-50">
                    {imgLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Aggiungi foto
                    <input type="file" accept="image/*" multiple className="hidden" data-testid="catalog-gallery-input"
                      onChange={(e) => uploadGallery(e.target.files)} />
                  </label>
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-slate-500">Varianti AI:</span>
                  {[1, 2, 3, 4].map((n) => (
                    <button key={n} type="button" onClick={() => setVarCount(n)}
                      data-testid={`catalog-varcount-${n}`}
                      className={`text-xs font-semibold w-7 h-7 rounded-full border ${varCount === n ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{n}</button>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={genVariations}
                    disabled={varLoading || (!form.image && !(form.gallery || []).length)} data-testid="catalog-gen-variations">
                    {varLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                    Genera {varCount} varianti dalle foto
                  </Button>
                </div>
                <p className="text-[11px] text-slate-400 mt-1.5">L'AI crea nuove inquadrature/angoli dello stesso prodotto a partire dalle foto reali caricate.</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving} data-testid="catalog-form-save" className="bg-blue-600 hover:bg-blue-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />} Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-catalog-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo prodotto?</AlertDialogTitle>
            <AlertDialogDescription>
              Il prodotto verrà rimosso definitivamente dal catalogo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-catalog-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemove} disabled={deleting}
              data-testid="delete-catalog-confirm"
              className="bg-red-600 hover:bg-red-700">
              {deleting ? "Eliminazione…" : "Elimina"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
